import { useId, useMemo, useState, type MouseEvent } from "react";
import AccessTimeOutlined from "@mui/icons-material/AccessTimeOutlined";
import ArrowForward from "@mui/icons-material/ArrowForward";
import Close from "@mui/icons-material/Close";
import Download from "@mui/icons-material/Download";
import FolderZip from "@mui/icons-material/FolderZip";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import Search from "@mui/icons-material/Search";
import SummarizeOutlined from "@mui/icons-material/SummarizeOutlined";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import {
  Box,
  Button,
  CircularProgress,
  DialogContent,
  DialogTitle,
  FormLabel,
  IconButton,
  InputAdornment,
  ListItemIcon,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTick } from "@wojtekmaj/react-hooks";
import { debug as debugFn } from "debug";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import z from "zod";

import { dayjs, IncidentWorkItemStatusSchema } from "@thalamos/common";
import { triggerDownload } from "@thalamos/frontend";
import type {
  CreateIncidentMinimumFields,
  FormBuilderField,
  IncidentForm,
  IncidentWorkItemIdUnbranded,
  IncidentWorkItemParticipantEnhancedUnbranded,
  IncidentWorkItemWithContext,
  MinimumFieldsPerSection,
} from "@thalamos/vision-common";
import {
  buildIncidentFormPdfFilename,
  calculateActions,
  calculateIncidentWorkItemStatus,
  dateSchema,
  FieldBuilder,
  FormSectionSchema,
  getCommandUnit,
  getDispatchReferenceForWorkflow,
  getIncidentTypeSelection,
  getLatestPlaceOfSafetyName,
  getTimeAtPlaceOfSafety,
  IncidentFormTypeSchema,
  incidentHasTransferRequest,
  IncidentTypeSchema,
  isCurrentUserSupervisor,
  OrganisationRoleSchema,
  ParticipantRoleSchema,
  ParticipantStatusSchema,
  tk,
  WorkflowVersionSchema,
} from "@thalamos/vision-common";

import api, { apiHooks } from "../../../api/index.js";
import { createUserSearch } from "../../../api/user/createUserSearch.js";
import { useUser } from "../../../Context/UserContext.js";
import { useToastNotifications } from "../../../hooks/useToastNotifications.js";
import { CancelTransferModal } from "../../../modals/CancelTransfer/CancelTransferModal.js";
import {
  DownloadExtractModal,
  SupervisorDownloadExtractModal,
} from "../../../modals/DownloadExtract/DownloadExtractModal.js";
import { InitiateTransferModal } from "../../../modals/InitiateTransfer/InitiateTransfer.js";
import { getIncidentStatus } from "../../../pages/FormProgress/FormProgress.js";
import { formatDuration } from "../../../pages/IncidentSummary/helpers/formatDuration.js";
import { createRoute } from "../../../routing/createRoute.js";
import { PopupDialog } from "../../Dialog/PopupDialog.js";
import { SinglePageForm } from "../../FormBuilder/SinglePageForm/SinglePageForm.js";
import { renderUkDateTime } from "../../util.js";

const debug = debugFn("vision-frontend:Incident");

const FilterSchema = z.object({
  createdDate: new FieldBuilder().withSchema(dateSchema.optional()).build()
    .schema,
  subDivisionIds: z
    .array(
      z.object({
        value: z.string(),
        valueLabel: z.string(),
      }),
    )
    .optional(),
});

export type FilterFormData = z.infer<typeof FilterSchema>;

function FilterModal({
  open,
  close,
  onSubmit,
  onClear,
  createdDateFilterQuery,
  subDivisionIdsFilterQuery,
}: {
  open: boolean;
  close: () => void;
  onSubmit: (values: {
    createdDate?: string;
    subDivisionIds?: { value: string; valueLabel: string }[];
  }) => void;
  onClear: () => void;
  createdDateFilterQuery?: string;
  subDivisionIdsFilterQuery?: { value: string; valueLabel: string }[];
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();

  const [{ data, loading: organisationsLoading }] =
    apiHooks.organisation.search("", 1000);

  const organisations = useMemo(() => {
    if (organisationsLoading) {
      return [];
    }

    const geographicUnitOrganisations = data?.results.filter((organisation) => {
      return (
        organisation.role ===
          OrganisationRoleSchema.enum.PoliceGeographicUnit ||
        organisation.role === OrganisationRoleSchema.enum.PoliceCommandUnit
      );
    });

    const formattedOptions = geographicUnitOrganisations?.map((org) => {
      return { value: org.id, label: org.name };
    });
    return formattedOptions ?? [];
  }, [data?.results, organisationsLoading]);

  const fields: FormBuilderField<
    FilterFormData,
    "createdDate" | "subDivisionIds"
  >[] = [];

  fields.push({
    type: "date",
    fieldName: "createdDate",
    label: tk("admin.incidents.incidentCreatedDate"),
    disableFuture: true,
  });

  fields.push({
    type: "multi-select",
    fieldName: "subDivisionIds",
    label: tk("admin.incidents.organisations"),
    options: organisations,
  });

  return (
    <PopupDialog
      open={open}
      onClose={close}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      disableDialogRootPadding={true}
    >
      <DialogTitle sx={{ fontSize: "1.250rem", fontWeight: "bold" }}>
        {t("admin.incidents.filters")}
        <IconButton
          aria-label="close"
          onClick={close}
          edge="end"
          sx={{
            position: "absolute",
            right: 15,
            top: 8,
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <SinglePageForm<typeof FilterSchema>
          schema={FilterSchema}
          initialValues={{
            createdDate: { value: createdDateFilterQuery ?? "" },
            subDivisionIds: subDivisionIdsFilterQuery,
          }}
          onSubmit={(values) => {
            onSubmit({
              createdDate: values.createdDate.value,
              subDivisionIds: values.subDivisionIds,
            });
            close();
          }}
          content={{ fields }}
          canCancel={true}
          onCancel={() => {
            onClear();
            close();
          }}
          confirmButtonLabel={tk("admin.incidents.apply")}
          cancelButtonLabel={tk("admin.incidents.clear")}
          ariaLabel="Filter"
        />
      </DialogContent>
    </PopupDialog>
  );
}

const getIncidentType = (
  incidentWorkItem: IncidentWorkItemWithContext,
  t: TFunction,
) => {
  const whatWillYouDo = getIncidentTypeSelection({ incidentWorkItem }).unwrapOr(
    undefined,
  );

  if (whatWillYouDo === IncidentTypeSchema.enum.section136) {
    return t("admin.incidents.section136");
  }
  if (whatWillYouDo === IncidentTypeSchema.enum.other) {
    return t("admin.incidents.other");
  }
  if (whatWillYouDo === IncidentTypeSchema.enum.mentalCapacityAct) {
    return t("admin.incidents.sectionMca");
  }
  if (whatWillYouDo === IncidentTypeSchema.enum.voluntary) {
    return t("admin.incidents.voluntary");
  }

  return t("admin.incidents.unknown");
};

const getSectorId = (forms: IncidentForm[]): string | undefined => {
  const createIncidentForm = forms.find(
    (form) => form.formType === IncidentFormTypeSchema.enum.createIncident,
  );
  if (!createIncidentForm) return;
  const createIncidentMainSection = createIncidentForm.sections.find(
    (section) => section.id === FormSectionSchema.enum.createIncidentMain,
  );
  if (!createIncidentMainSection || !createIncidentMainSection.data) return;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (createIncidentMainSection.data as any)?.geographicUnit?.valueLabel;
};

/**
 * A workflow version names the force and then the version of that force's
 * workflow, so the force is everything before the dash. Comparing whole version
 * strings would mislabel incidents on any version added later.
 */
const forceOfWorkflow = (workflowVersion: string): string => {
  const separator = workflowVersion.indexOf("-");
  return separator === -1
    ? workflowVersion
    : workflowVersion.slice(0, separator);
};

const isBtpWorkflow = (workflowVersion: string): boolean =>
  forceOfWorkflow(workflowVersion) ===
  forceOfWorkflow(WorkflowVersionSchema.enum.BTP_1_0);

const getForceDisplayName = (workflowVersion: string): string => {
  if (isBtpWorkflow(workflowVersion)) {
    return "BTP";
  }
  if (
    forceOfWorkflow(workflowVersion) ===
    forceOfWorkflow(WorkflowVersionSchema.enum.MET_1_0)
  ) {
    return "MPS";
  }
  if (
    forceOfWorkflow(workflowVersion) ===
    forceOfWorkflow(WorkflowVersionSchema.enum.COLP_1_0)
  ) {
    return "COLP";
  }
  return workflowVersion;
};

const getDivisionName = (
  incident: IncidentWorkItemWithContext,
  t: TFunction,
): string => {
  const { workflowVersion } = incident.incidentWorkItem;
  if (isBtpWorkflow(workflowVersion)) {
    // BTP uses geographicUnit for division
    return getSectorId(incident.forms) ?? t("admin.incidents.unknown");
  }
  // MPS and COLP use commandUnit for division
  const commandUnit = getCommandUnit({ incidentWorkItem: incident });
  return commandUnit?.name ?? t("admin.incidents.unknown");
};

const getOwner = (
  participants: IncidentWorkItemWithContext["participants"],
): IncidentWorkItemParticipantEnhancedUnbranded | undefined => {
  const owner = participants.find(
    (participant) => participant.role === ParticipantRoleSchema.enum.owner,
  );

  return owner;
};

const getResponsibleOfficerEmail = (
  participants: IncidentWorkItemWithContext["participants"],
): string | undefined => {
  const responsibleOfficer = getOwner(participants);
  if (!responsibleOfficer) return;

  return responsibleOfficer.email;
};

const getCWnumber = (incidentWorkItem: IncidentWorkItemWithContext) => {
  if (incidentWorkItem.forms.length === 0) return;
  const createIncidentForm = incidentWorkItem.forms.find(
    (form) => form.formType === IncidentFormTypeSchema.enum.createIncident,
  );
  if (createIncidentForm === undefined) return;

  const createIncidentMainSection = createIncidentForm.sections.find(
    (section) => section.id === FormSectionSchema.enum.createIncidentMain,
  );
  if (createIncidentMainSection === undefined) return;

  return (
    createIncidentMainSection.data as CreateIncidentMinimumFields | undefined
  )?.dispatchNumber.value;
};

const getDispatchNumberCreated = (
  forms: IncidentForm[],
): string | undefined => {
  const createIncidentForm = forms.find(
    (form) => form.formType === IncidentFormTypeSchema.enum.createIncident,
  );
  if (!createIncidentForm) return;

  const createIncidentMainSection = createIncidentForm.sections.find(
    (section) => section.id === FormSectionSchema.enum.createIncidentMain,
  );
  if (!createIncidentMainSection || !createIncidentMainSection.data) return;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (createIncidentMainSection.data as any)?.dispatchNumberCreated?.value;
};

const getArrivalDates = (incident: IncidentWorkItemWithContext): Date[] => {
  const posForms = incident.forms.filter(
    (form) => form.formType === IncidentFormTypeSchema.enum.recordPlaceOfSafety,
  );

  const arrivalDates: Date[] = posForms.flatMap((form) =>
    form.sections
      .filter(
        (section) =>
          section.id === FormSectionSchema.enum.recordPlaceOfSafetyArrival,
      )
      .map((section) => {
        const data = section.data as
          | MinimumFieldsPerSection[typeof FormSectionSchema.enum.recordPlaceOfSafetyArrival]
          | undefined;

        const value = data?.arrivalDateTime?.value;
        if (!value) return null;

        const parsed = dayjs(value);
        return parsed.isValid() ? parsed.toDate() : null;
      })
      .filter((date): date is Date => date !== null),
  );

  return arrivalDates;
};

const getLatestArrivalDate = (
  incident: IncidentWorkItemWithContext,
): Date | null => {
  const dates = getArrivalDates(incident);
  if (dates.length === 0) return null;

  return dates.reduce((latest, current) =>
    current > latest ? current : latest,
  );
};

function IncidentRow({
  incident,
  bgColor,
  refetchWorkItems,
  showSectorIdColumn,
  amberThresholdMinutes,
  redThresholdMinutes,
  canView136Duration,
  isAdminView = false,
}: {
  incident: IncidentWorkItemWithContext;
  bgColor: string;
  refetchWorkItems: ReturnType<typeof apiHooks.incidentWorkItem.search>[1];
  showSectorIdColumn: boolean;
  amberThresholdMinutes: number | undefined;
  redThresholdMinutes: number | undefined;
  canView136Duration: boolean;
  isAdminView?: boolean;
}) {
  const { user } = useUser();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showCancelTransferModal, setShowCancelTransferModal] = useState(false);
  const [modalData, setModalData] = useState<{
    receivingOfficer: string;
    incidentWorkItemId: IncidentWorkItemIdUnbranded;
  } | null>(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [isDownloadingSnapshots, setIsDownloadingSnapshots] = useState(false);
  const { t } = useTranslation();
  const theme = useTheme();
  const notifications = useToastNotifications();
  const { incidentWorkItem, forms, participants } = incident;

  const organisationConfig = user?.sessionOrganisationConfiguration ?? {};
  const snapshotDownloadEnabled = organisationConfig.snapshotDownloadEnabled;
  const incidentSummaryViewEnabled =
    organisationConfig.incidentSummaryViewEnabled;

  const incidentStatus = getIncidentStatus(incident, t);
  const incidentType = getIncidentType(incident, t);
  const force = getForceDisplayName(incident.incidentWorkItem.workflowVersion);
  const division = getDivisionName(incident, t);
  const latestPos =
    getLatestPlaceOfSafetyName(incident) ?? t("admin.incidents.notAdded");
  const sectorId = getSectorId(forms) ?? t("admin.incidents.unknown");

  const responsibleOfficerEmail =
    getResponsibleOfficerEmail(participants) ?? t("admin.incidents.unknown");

  const hasTransferRequest = incidentHasTransferRequest({
    incidentWorkItem: incident,
  });

  const isEndedIncident =
    calculateIncidentWorkItemStatus(incident).unwrapOr(
      IncidentWorkItemStatusSchema.enum.unknown,
    ) === IncidentWorkItemStatusSchema.enum.ended;

  const open = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const actions = user
    ? calculateActions({
        incidentWorkItem: incident,
        user,
        asOfDateTime: dayjs(),
      })
    : undefined;

  const cwNumber = getCWnumber(incident);

  const dispatchNumberCreated = getDispatchNumberCreated(forms);

  const combinedDispatch = cwNumber
    ? getDispatchReferenceForWorkflow(
        incident.incidentWorkItem.workflowVersion,
        { value: cwNumber },
        dispatchNumberCreated ? { value: dispatchNumberCreated } : undefined,
      )
    : t("admin.incidents.notAdded");

  const hasBeenAnonymised =
    incident.incidentWorkItem.anonymiserVersion !== null;

  const arrivalTime = getLatestArrivalDate(incident);
  const timeAtPlaceOfSafety = getTimeAtPlaceOfSafety(incident, dayjs());

  const incidentTypeSelection = getIncidentTypeSelection({
    incidentWorkItem: incident,
  }).unwrapOr(undefined);

  let durationBgColor = "transparent";
  if (timeAtPlaceOfSafety && !isEndedIncident && !hasBeenAnonymised) {
    if (
      redThresholdMinutes !== undefined &&
      timeAtPlaceOfSafety.minutes > redThresholdMinutes
    ) {
      durationBgColor = theme.palette.custom.error.main;
    } else if (
      amberThresholdMinutes !== undefined &&
      timeAtPlaceOfSafety.minutes > amberThresholdMinutes
    ) {
      durationBgColor = theme.palette.custom.warning.main;
    }
  }

  const isPolicePresenceRequested =
    calculateIncidentWorkItemStatus(incident).unwrapOr(
      IncidentWorkItemStatusSchema.enum.unknown,
    ) === IncidentWorkItemStatusSchema.enum["police-presence-requested"];

  return (
    <>
      <TableRow
        key={incidentWorkItem.id}
        sx={{
          backgroundColor: bgColor,
        }}
      >
        {isAdminView ? (
          <>
            <TableCell>{force}</TableCell>
            <TableCell>{division}</TableCell>
          </>
        ) : (
          <TableCell>{incidentType}</TableCell>
        )}
        <TableCell
          sx={{
            backgroundColor: isPolicePresenceRequested
              ? theme.palette.banner.background
              : "transparent",
            p: theme.spacing(1.5, 2),
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              height: "100%",
            }}
          >
            {hasBeenAnonymised && (
              <VisibilityOff fontSize="small" color="inherit" />
            )}
            {isPolicePresenceRequested && (
              <WarningAmberOutlined fontSize="small" />
            )}
            <Typography
              variant="body2"
              sx={{
                fontWeight: isPolicePresenceRequested ? 600 : 400,
              }}
            >
              {incidentStatus}
            </Typography>
          </Box>
        </TableCell>

        <TableCell>{combinedDispatch}</TableCell>
        <TableCell>
          {responsibleOfficerEmail}
          {hasTransferRequest && (
            <ArrowForward
              fontSize="small"
              sx={{
                verticalAlign: "middle",
                marginLeft: "0.5em",
              }}
            />
          )}
        </TableCell>
        <TableCell>
          <span>
            {arrivalTime
              ? renderUkDateTime(dayjs(arrivalTime).toDate(), true)
              : t("admin.incidents.notArrived")}
          </span>
        </TableCell>
        {canView136Duration && (
          <TableCell
            data-testid="duration-cell"
            className={
              durationBgColor === theme.palette.custom.warning.main
                ? "duration-amber"
                : durationBgColor === theme.palette.custom.error.main
                  ? "duration-red"
                  : undefined
            }
            sx={{
              backgroundColor: durationBgColor,
            }}
          >
            {/*
              Duration is intentionally shown for anonymised incidents if the data is available,
              as the anonymisation process preserves timing data and users still benefit from seeing it.
            */}
            {timeAtPlaceOfSafety ? (
              <>
                <AccessTimeOutlined
                  sx={{
                    verticalAlign: "middle",
                    mr: 0.5,
                  }}
                  fontSize="small"
                />{" "}
                {formatDuration(timeAtPlaceOfSafety.minutes)}
              </>
            ) : (
              "-"
            )}
          </TableCell>
        )}
        <TableCell>{showSectorIdColumn ? (sectorId ?? "") : null}</TableCell>
        <TableCell>
          <span>{latestPos}</span>
        </TableCell>
        <TableCell>
          <Button variant="outlined" onClick={handleClick}>
            {t("admin.incidents.action")}
          </Button>
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            {incidentSummaryViewEnabled && (
              <MenuItem
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  textTransform: "uppercase",
                }}
                onClick={() => {
                  navigate(
                    createRoute.incidentSummaryPage(incidentWorkItem.id),
                  );
                }}
                data-testid="view-summary-btn"
              >
                <ListItemIcon>
                  <SummarizeOutlined fontSize="small" color="primary" />
                </ListItemIcon>
                {t(tk("incidentSummary.title"))}
              </MenuItem>
            )}
            <MenuItem
              disabled={hasTransferRequest || isEndedIncident}
              sx={{
                color:
                  hasTransferRequest || isEndedIncident
                    ? theme.palette.action.disabled
                    : theme.palette.primary.main,
                fontWeight: 600,
                textTransform: "uppercase",
              }}
              onClick={() => {
                setShowTransferModal(true);
              }}
            >
              <ListItemIcon>
                <ArrowForward fontSize="small" color="primary" />
              </ListItemIcon>

              {t("admin.incidents.transfer")}
            </MenuItem>

            <MenuItem
              disabled={!hasTransferRequest || isEndedIncident}
              sx={{
                color:
                  !hasTransferRequest || isEndedIncident
                    ? theme.palette.action.disabled
                    : theme.palette.primary.main,
                fontWeight: 600,
                textTransform: "uppercase",
              }}
              onClick={() => {
                const transferRequest = participants.find((participant) => {
                  return (
                    participant.status ===
                    ParticipantStatusSchema.enum.pendingTransfer
                  );
                });

                if (!transferRequest) {
                  notifications.show(
                    t("incidentWorkItem.notificationCancelledError"),
                    { severity: "error" },
                  );
                  return;
                }
                const receivingOfficer = transferRequest.name;
                const incidentWorkItemId = incidentWorkItem.id;
                setModalData({ receivingOfficer, incidentWorkItemId });
                setShowCancelTransferModal(true);
              }}
            >
              <ListItemIcon>
                <Close fontSize="small" color="primary" />
              </ListItemIcon>

              {t("admin.incidents.cancelTransfer")}
            </MenuItem>
            <MenuItem
              disabled={!actions?.pdf.canDownloadAsSupervisor}
              sx={{
                color: !actions?.pdf.canDownloadAsSupervisor
                  ? theme.palette.action.disabled
                  : theme.palette.primary.main,
                fontWeight: 600,
                textTransform: "uppercase",
              }}
              onClick={() => {
                void (async () => {
                  try {
                    const { id } = incident.incidentWorkItem;
                    const response = await api.incidentWorkItem.getPdf(id);

                    if (response.status !== 200) {
                      debug("Failed to download PDF. Response:", response);
                      notifications.show(
                        t("admin.incidents.downloadPdfFailed"),
                        { severity: "error" },
                      );
                      return;
                    }

                    const dataUri = `data:application/pdf;base64,${response.data}`;
                    triggerDownload(
                      dataUri,
                      buildIncidentFormPdfFilename({
                        incidentType: incidentTypeSelection,
                        date: new Date(),
                      }),
                    );
                  } catch (error) {
                    debug("Error downloading PDF:", error);

                    notifications.show(t("admin.incidents.downloadPdfError"), {
                      severity: "error",
                    });
                  }
                })();
              }}
            >
              <ListItemIcon>
                <Download fontSize="small" color="primary" />
              </ListItemIcon>

              {t("admin.incidents.downloadPdf")}
            </MenuItem>
            {snapshotDownloadEnabled && (
              <MenuItem
                disabled={
                  !actions?.pdf.canDownloadAsSupervisor ||
                  isDownloadingSnapshots
                }
                sx={{
                  color:
                    !actions?.pdf.canDownloadAsSupervisor ||
                    isDownloadingSnapshots
                      ? theme.palette.action.disabled
                      : theme.palette.primary.main,
                  fontWeight: 600,
                  textTransform: "uppercase",
                }}
                onClick={() => {
                  void (async () => {
                    const { id } = incident.incidentWorkItem;
                    setIsDownloadingSnapshots(true);

                    try {
                      // Show info toast that download is starting
                      const infoToastKey = notifications.show(
                        t("admin.incidents.downloadSnapshotsStarted"),
                        { severity: "info" },
                      );

                      // Start the snapshot download job
                      const createResponse =
                        await api.incidentWorkItem.createSnapshotDownload(id);

                      if (createResponse.status !== 200) {
                        notifications.close(infoToastKey);
                        notifications.show(
                          t("admin.incidents.downloadSnapshotsFailed"),
                          { severity: "error" },
                        );
                        setIsDownloadingSnapshots(false);
                        return;
                      }

                      const jobId = createResponse.data.downloadId;

                      // Poll for completion
                      const pollInterval = 2000;
                      const maxPolls = 60; // 2 minutes max
                      let pollCount = 0;

                      const poll = async (): Promise<void> => {
                        pollCount++;
                        const pollResponse =
                          await api.incidentWorkItem.pollSnapshotDownload(
                            id,
                            jobId,
                          );

                        if (pollResponse.data.status === "pending") {
                          if (pollCount >= maxPolls) {
                            notifications.close(infoToastKey);
                            notifications.show(
                              t("admin.incidents.downloadSnapshotsTimeout"),
                              { severity: "error" },
                            );
                            setIsDownloadingSnapshots(false);
                            return;
                          }
                          await new Promise((resolve) =>
                            setTimeout(resolve, pollInterval),
                          );
                          return poll();
                        }

                        if (pollResponse.data.status === "failed") {
                          notifications.close(infoToastKey);
                          notifications.show(
                            pollResponse.data.errorMessage ||
                              t("admin.incidents.downloadSnapshotsFailed"),
                            { severity: "error" },
                          );
                          setIsDownloadingSnapshots(false);
                          return;
                        }

                        if (pollResponse.data.status === "completed") {
                          // Download the file
                          notifications.close(infoToastKey);
                          notifications.show(
                            t("admin.incidents.downloadSnapshotsReady"),
                            { severity: "success" },
                          );
                          window.open(pollResponse.data.downloadUrl, "_blank");
                          setIsDownloadingSnapshots(false);
                        }
                      };

                      await poll();
                    } catch (error) {
                      debug("Error downloading snapshots:", error);
                      notifications.show(
                        t("admin.incidents.downloadSnapshotsError"),
                        {
                          severity: "error",
                        },
                      );
                      setIsDownloadingSnapshots(false);
                    }
                  })();
                }}
              >
                <ListItemIcon>
                  {isDownloadingSnapshots ? (
                    <CircularProgress size={20} />
                  ) : (
                    <FolderZip fontSize="small" color="primary" />
                  )}
                </ListItemIcon>

                {isDownloadingSnapshots
                  ? t("admin.incidents.downloadingSnapshots")
                  : t("admin.incidents.downloadWithSnapshots")}
              </MenuItem>
            )}
          </Menu>
        </TableCell>
      </TableRow>
      {showCancelTransferModal && modalData && (
        <CancelTransferModal
          open={showCancelTransferModal}
          close={() => {
            setModalData(null);
            setShowCancelTransferModal(false);
          }}
          receivingOfficer={modalData.receivingOfficer}
          incidentWorkItemId={modalData.incidentWorkItemId}
          onTransferCancelled={async () => {
            await refetchWorkItems();
          }}
        />
      )}

      {showTransferModal && (
        <InitiateTransferModal
          open={showTransferModal}
          onClose={() => {
            setShowTransferModal(false);
          }}
          incidentWorkItemId={incidentWorkItem.id}
          searchUsers={createUserSearch(
            getCommandUnit({ incidentWorkItem: incident })?.id,
          )}
          isAdmin={true}
          onConfirm={async (email: string | undefined) => {
            if (incidentWorkItem.id === undefined || email === undefined)
              return;

            const createTransfer = {
              receivingOfficerEmail: email,
              incidentWorkItemId: incidentWorkItem.id,
            };

            await api.incidentWorkItem
              .createTransfer(createTransfer)
              .then(async (res) => {
                if (res.status === 204) {
                  notifications.show(
                    t("transfer.initiateTransfer.confirmToast", { email }),
                    {
                      severity: "success",
                    },
                  );
                  setShowTransferModal(false);
                  await refetchWorkItems();
                } else {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                  notifications.show(res.data.reason, {
                    severity: "error",
                  });
                }
              });
          }}
        />
      )}
    </>
  );
}

export const IncidentInner = ({
  incidents,
  page,
  setPage,
  numPages,
  refetchWorkItems,
  cadLabel,
  amberThresholdHours,
  redThresholdHours,
  canView136Duration,
  isAdminView = false,
}: {
  incidents: IncidentWorkItemWithContext[];
  page: number;
  setPage: (page: number) => void;
  numPages: number;
  refetchWorkItems: ReturnType<typeof apiHooks.incidentWorkItem.search>[1];
  cadLabel: string | undefined;
  amberThresholdHours: number | undefined;
  redThresholdHours: number | undefined;
  canView136Duration: boolean;
  isAdminView?: boolean;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const tick = useTick(60_000);
  const refreshKey = canView136Duration ? tick : 0;

  const sortedIncidents = useMemo(() => {
    return [...incidents].sort((a, b) => {
      const timeA = getLatestArrivalDate(a) || a.incidentWorkItem.createdAt;
      const timeB = getLatestArrivalDate(b) || b.incidentWorkItem.createdAt;
      return new Date(timeB).getTime() - new Date(timeA).getTime();
    });
  }, [incidents, refreshKey]);

  const showSectorIdColumn = useMemo(() => {
    if (isAdminView) return false;
    return incidents.some((incident) => {
      const sector = getSectorId(incident.forms);
      return sector !== undefined && sector !== null;
    });
  }, [incidents, isAdminView]);

  const amberThresholdMinutes = amberThresholdHours
    ? amberThresholdHours * 60
    : undefined;
  const redThresholdMinutes = redThresholdHours
    ? redThresholdHours * 60
    : undefined;

  return (
    <Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ verticalAlign: "top" }}>
              {/* Incident Type (supervisor view) / Force + Division (admin view) */}
              {isAdminView ? (
                <>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("admin.incidents.force")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("admin.incidents.division")}
                  </TableCell>
                </>
              ) : (
                <TableCell sx={{ fontWeight: 700 }}>
                  {t("admin.incidents.incidentType")}
                </TableCell>
              )}

              {/* Status */}
              <TableCell sx={{ fontWeight: 700 }}>
                {t("admin.incidents.incidentStatus")}
              </TableCell>

              {/* CAD / Incident Number */}
              <TableCell sx={{ fontWeight: 700 }}>{cadLabel}</TableCell>

              {/* Responsible Officer */}
              <TableCell sx={{ fontWeight: 700 }}>
                {t("admin.incidents.responsibleOfficer")}
              </TableCell>

              {/* Arrival Time */}
              <TableCell sx={{ fontWeight: 700 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {t("admin.incidents.arrivalTime")}
                  <Tooltip title={t("admin.incidents.posArrivalTooltip")}>
                    <InfoOutlined fontSize="small" color="action" />
                  </Tooltip>
                </Box>
              </TableCell>

              {/* S136 Duration */}
              {canView136Duration && (
                <TableCell sx={{ fontWeight: 700 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    {t("admin.incidents.duration")}
                    <Tooltip title={t("admin.incidents.s136DurationTooltip")}>
                      <InfoOutlined fontSize="small" color="action" />
                    </Tooltip>
                  </Box>
                </TableCell>
              )}

              {/* Sector ID (optional) */}
              <TableCell sx={{ fontWeight: 700 }}>
                {showSectorIdColumn ? t("admin.incidents.sectorId") : ""}
              </TableCell>

              {/* Latest Place of Safety */}
              <TableCell sx={{ fontWeight: 700 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  {t("admin.incidents.latestPos")}
                  <Tooltip title={t("admin.incidents.latestPosTooltip")}>
                    <InfoOutlined fontSize="small" color="action" />
                  </Tooltip>
                </Box>
              </TableCell>

              {/* Actions */}
              <TableCell sx={{ fontWeight: 700, width: "10%" }}>
                {t("admin.incidents.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedIncidents.map(
              (incident: IncidentWorkItemWithContext, index) => {
                return (
                  <IncidentRow
                    bgColor={
                      index % 2 === 0 ? theme.palette.secondary.main : "white"
                    }
                    key={incident.incidentWorkItem.id}
                    incident={incident}
                    refetchWorkItems={refetchWorkItems}
                    showSectorIdColumn={showSectorIdColumn}
                    amberThresholdMinutes={amberThresholdMinutes}
                    redThresholdMinutes={redThresholdMinutes}
                    canView136Duration={canView136Duration}
                    isAdminView={isAdminView}
                  />
                );
              },
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          paddingTop: "3em",
        }}
      >
        <button
          disabled={page === 0}
          onClick={() => {
            if (page === 0) {
              return;
            }
            setPage(page - 1);
          }}
        >
          &#60;
        </button>
        <Box sx={{ marginLeft: "1em", marginRight: "1em" }}>
          Page {page + 1} of {numPages}
        </Box>
        <button
          disabled={page >= numPages - 1}
          onClick={() => {
            if (page < numPages - 1) {
              setPage(page + 1);
            }
          }}
        >
          &#62;
        </button>
      </Box>
    </Box>
  );
};

export function IncidentManagement() {
  const { t } = useTranslation();
  const { user } = useUser();
  const ariaIdOfficerName = useId();
  const ariaIdIncidentNumber = useId();

  const [officerNameFilterQuery, setOfficerNameFilterQuery] = useState("");
  const [dispatchNumberFilterQuery, setDispatchNumberFilterQuery] =
    useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [page, setPage] = useState(0);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [createdDateFilterQuery, setCreatedDateFilterQuery] = useState("");
  const [subDivisionIdsFilterQuery, setSubDivisionIdsFilterQuery] = useState<
    { value: string; valueLabel: string }[]
  >([]);

  const { amberS136ThresholdHours, redS136ThresholdHours } =
    user?.sessionOrganisationConfiguration || {};

  const incidentPageSize = 20;
  const [{ data: incidents, loading }, refetchWorkItems] =
    apiHooks.incidentWorkItem.search({
      officerName: officerNameFilterQuery,
      dispatchNumber: dispatchNumberFilterQuery,
      createdDate: createdDateFilterQuery,
      subDivisionIds: subDivisionIdsFilterQuery.map((org) => org.value),
      limit: incidentPageSize,
      offset: page * incidentPageSize,
    });

  const numPages =
    incidents && incidents.count
      ? Math.ceil(incidents.count / incidentPageSize)
      : 1;

  const userOrganisationName =
    user && isCurrentUserSupervisor(user)
      ? user.sessionContext?.organisationName
      : "";

  const handleApplyFilters = async (newFilters: {
    createdDate?: string;
    subDivisionIds?: { value: string; valueLabel: string }[];
  }) => {
    setCreatedDateFilterQuery(newFilters.createdDate ?? "");
    if (newFilters.subDivisionIds && newFilters.subDivisionIds.length > 0) {
      setSubDivisionIdsFilterQuery(newFilters.subDivisionIds);
    } else {
      setSubDivisionIdsFilterQuery([]);
    }
    await refetchWorkItems();
  };

  const handleClearFilters = async () => {
    setCreatedDateFilterQuery("");
    setSubDivisionIdsFilterQuery([]);
    await refetchWorkItems();
  };

  const canView136Duration =
    user != null &&
    (amberS136ThresholdHours !== undefined ||
      redS136ThresholdHours !== undefined);

  return (
    <Box sx={{ paddingX: 2, paddingY: 3 }}>
      <Typography
        variant="h6"
        component="div"
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          marginBottom: 3,
        }}
      >
        {userOrganisationName}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          marginBottom: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "flex-end",
          }}
        >
          <Box sx={{ minWidth: 200, display: "flex", flexDirection: "column" }}>
            <FormLabel
              sx={{ fontWeight: "bold", marginBottom: 1 }}
              htmlFor={ariaIdOfficerName}
            >
              {t("admin.incidents.searchOfficerNameLabel")}
            </FormLabel>
            <TextField
              id={ariaIdOfficerName}
              value={officerNameFilterQuery}
              onChange={(e) => {
                setOfficerNameFilterQuery(e.target.value);
                setPage(0);
              }}
              size="small"
              fullWidth
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>

          <Box sx={{ minWidth: 200, display: "flex", flexDirection: "column" }}>
            <FormLabel
              sx={{ fontWeight: "bold", marginBottom: 1 }}
              htmlFor={ariaIdIncidentNumber}
            >
              {t("admin.incidents.incidentNumber")}
            </FormLabel>
            <TextField
              id={ariaIdIncidentNumber}
              value={dispatchNumberFilterQuery}
              onChange={(e) => {
                setDispatchNumberFilterQuery(e.target.value);
                setPage(0);
              }}
              size="small"
              fullWidth
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>

          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setShowFilter(!showFilter);
            }}
            sx={{ height: "fit-content" }}
          >
            {t("admin.incidents.filters")}
          </Button>
        </Box>

        <Box sx={{ flexShrink: 0 }}>
          <Button
            color="primary"
            variant="outlined"
            endIcon={<Download />}
            onClick={() => {
              setShowDownloadModal(true);
            }}
          >
            {t("admin.incidents.downloadExtract")}
          </Button>
        </Box>
      </Box>
      <Box sx={{ paddingY: 2 }}>
        {loading ? (
          <CircularProgress />
        ) : (
          <IncidentInner
            incidents={incidents?.results || []}
            page={page}
            setPage={setPage}
            numPages={numPages}
            refetchWorkItems={refetchWorkItems}
            cadLabel={t("admin.incidents.incidentNumber")}
            amberThresholdHours={amberS136ThresholdHours}
            redThresholdHours={redS136ThresholdHours}
            canView136Duration={canView136Duration}
            isAdminView={!!user?.isAdminSession}
          />
        )}
      </Box>
      {showDownloadModal && user && !isCurrentUserSupervisor(user) && (
        <DownloadExtractModal
          open={showDownloadModal}
          close={() => {
            setShowDownloadModal(false);
          }}
        />
      )}
      {showDownloadModal && user && isCurrentUserSupervisor(user) && (
        <SupervisorDownloadExtractModal
          open={showDownloadModal}
          close={() => {
            setShowDownloadModal(false);
          }}
          user={user}
        />
      )}
      {showFilter && (
        <FilterModal
          open={showFilter}
          close={() => {
            setShowFilter(false);
          }}
          onSubmit={(values) => void handleApplyFilters(values)}
          createdDateFilterQuery={createdDateFilterQuery}
          subDivisionIdsFilterQuery={subDivisionIdsFilterQuery}
          onClear={() => void handleClearFilters()}
        />
      )}
    </Box>
  );
}
